import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import SiteShell from '../components/site/SiteShell'
import { PRIVACY_EMAIL } from '../lib/site'

/**
 * Privacy policy (`/privacy`) — public, no session required: this is the
 * URL Google Play's console and a first-time visitor both need to be able
 * to open cold. The Persian text mirrors docs/privacy-policy.md in the
 * tooti repo, the tracked source of truth; the English is a translation of
 * that same policy, not the design's own draft (which it marks "must be
 * reviewed by legal counsel"). Update all three together.
 *
 * The feed-consent wording in the feed section is quoted verbatim from
 * backend-nest/src/social/consent.ts (CONSENT_VERSION) rather than
 * paraphrased, so this page never claims something the app itself doesn't
 * already say at the point of consent.
 *
 * Frame from design "Tooti Web" `privacy`: badge, title, revision line,
 * then numbered sections; the last one a card with the data contact.
 */

const H2 = 'font-display text-[clamp(20px,3.8vw,25px)] font-extrabold'
const P = 'text-[15px] leading-[1.9] text-ink-2'
const CARD = 'flex flex-col gap-3 rounded-[22px] border-2 border-line bg-surface p-[22px] shadow-[0_6px_0_var(--color-line)]'

function Section({ id, number, title, children }) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-3">
      <h2 id={id} className={H2}>
        {number}. {title}
      </h2>
      {children}
    </section>
  )
}

export default function Privacy() {
  const { t, i18n } = useTranslation('privacy')
  const { t: site } = useTranslation('site')
  const rows = t('sections.data.rows', { returnObjects: true })
  const fa = (i18n.resolvedLanguage ?? i18n.language) === 'fa'
  const numbers = fa ? ['۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'] : ['1', '2', '3', '4', '5', '6', '7', '8', '9']

  return (
    <SiteShell title={site('pageTitle.privacy')}>
      <div className="mx-auto flex w-full max-w-[800px] flex-col gap-[30px] px-4 pb-[clamp(44px,8vw,72px)] pt-[clamp(30px,6vw,54px)] sm:px-5">
        <header className="flex flex-col gap-3.5" style={{ animation: 't-rise .5s ease-out both' }}>
          <span className="self-start rounded-full bg-sky-tint px-4 py-2 font-display text-[15px] font-extrabold text-sky-deep">{t('badge')}</span>
          <h1 className="font-display text-[clamp(30px,6.4vw,44px)] font-extrabold tracking-[-1.3px]">{t('title')}</h1>
          <p className="text-base leading-[1.85] text-ink-2 [text-wrap:pretty]">{t('lead')}</p>
          <span className="text-[13.5px] text-ink-2">{t('lastUpdated')}</span>
        </header>

        <Section id="privacy-controller" number={numbers[0]} title={t('sections.controller.title')}>
          <p className={P}>{t('sections.controller.body')}</p>
        </Section>

        <Section id="privacy-data" number={numbers[1]} title={t('sections.data.title')}>
          <div className="rounded-[22px] border-2 border-line bg-surface px-[22px] py-2">
            {rows.map(([label, body]) => (
              <div key={label} className="flex flex-col gap-1 border-b-2 border-line py-3 last:border-0 sm:flex-row sm:gap-4">
                <span className="shrink-0 font-bold text-ink sm:w-56">{label}</span>
                <span className="text-sm leading-7 text-ink-2">{body}</span>
              </div>
            ))}
          </div>
          <p className={P}>{t('sections.data.notCollected')}</p>
        </Section>

        <Section id="privacy-audio" number={numbers[2]} title={t('sections.audio.title')}>
          <p className={P}>{t('sections.audio.body')}</p>
        </Section>

        <Section id="privacy-feed" number={numbers[3]} title={t('sections.feed.title')}>
          <p className={P}>{t('sections.feed.body')}</p>
          <blockquote className="rounded-[18px] bg-surface-2 px-[18px] py-4 text-sm leading-7 text-ink-2">{t('sections.feed.quote')}</blockquote>
        </Section>

        <Section id="privacy-share" number={numbers[4]} title={t('sections.share.title')}>
          <p className={P}>
            <strong className="text-ink">{t('sections.share.azure')}</strong> — {t('sections.share.azureBody')}
            <br />
            <strong className="text-ink">{t('sections.share.sms')}</strong> — {t('sections.share.smsBody')}
          </p>
          <p className={P}>{t('sections.share.noSale')}</p>
        </Section>

        <Section id="privacy-security" number={numbers[5]} title={t('sections.security.title')}>
          <p className={P}>{t('sections.security.body')}</p>
        </Section>

        <Section id="privacy-rights" number={numbers[6]} title={t('sections.rights.title')}>
          <p className={P}>{t('sections.rights.body')}</p>
          {/* The public deletion page (tooti#150) — the URL Google Play's Data safety form points at. */}
          <Link
            to="/delete-account"
            className="site-btn inline-flex h-[52px] items-center justify-center self-start rounded-2xl bg-bad-tint px-[22px] font-display text-base font-extrabold text-bad-ink [--press:var(--color-line-2)]"
          >
            {t('sections.rights.deleteLink')}
          </Link>
        </Section>

        <Section id="privacy-children" number={numbers[7]} title={t('sections.children.title')}>
          <p className={P}>{t('sections.children.body')}</p>
        </Section>

        <section aria-labelledby="privacy-contact" className={CARD}>
          <h2 id="privacy-contact" className="font-display text-xl font-extrabold">
            {numbers[8]}. {t('sections.contact.title')}
          </h2>
          <p className="text-[15px] leading-[1.85] text-ink-2">
            {t('sections.contact.body')}{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} dir="ltr" className="font-bold text-brand-600 hover:underline">
              {PRIVACY_EMAIL}
            </a>
          </p>
          <Link
            to="/contact"
            className="site-btn inline-flex h-[52px] items-center justify-center self-start rounded-2xl bg-surface-2 px-[22px] font-display text-base font-extrabold text-ink [--press:var(--color-line-2)]"
          >
            {t('sections.contact.form')}
          </Link>
        </section>
      </div>
    </SiteShell>
  )
}
